/**
 * **The damage pipeline** (`event-engine-damage` D2): what one {@link DamagePacketEvent} does to
 * its target. The order is the book's, and no listener order touches it (`decisions/0212` item 2
 * exempts it):
 *
 *   1. **Exposed** doubles each physical instance, "before applying any reductions" (Core p.77).
 *   2. **Armor**, once for the packet, spent across its armorable instances in order: "ARMOR
 *      reduces all incoming damage from a single source" (p.67). AP, burn and heat skip it, and
 *      Shredded zeroes it (p.78).
 *   3. **Resistance** halves each instance of a resisted type, rounding up. Shredded denies it.
 *   4. **Other deductions** (Reduce), once for the packet, spilling across instances in order.
 *   5. **Immunity**: the instance lands as a flagged `damage` step and nothing else (p.68).
 *   6. **Overshield**, then HP or the heat track (p.105), through `routeFinalDamage`. Heat never
 *      touches the shield.
 *
 * A capped Resistance or Immunity the packet used is spent once, after its instances (R7, BC-077).
 * A Sniper's Mark packet (`substituteStructure`) skips all six and takes one structure point.
 *
 * The target is read off the state the packet lands on, so a packet that lands after another one
 * in the same resolution sees the Overshield and HP that one left, when the site hands it that
 * state. Each instance after the first lands on what the earlier ones left (BC-021).
 *
 * {@link applyDamagePacket} is the pure pipeline; {@link dealDamagePacket} raises the packet into
 * the intent's loop when the site holds one, and lands it directly when it doesn't.
 */
import { applyResolved, type Combatant, type DamageStep, type EncounterState, type ResolvedStep } from "@lancer-vtt/domain";
import type { RequestAttack } from "@lancer-vtt/shared";
import type { DiceSource } from "../../dice";
import type { RecordedAnswers } from "../effects/choices";
import type { LoadedOverlay } from "../effects/loader";
import { nonAttackArmor, nonAttackExposed, nonAttackGrantCaps, nonAttackImmune, nonAttackImmuneWarning, nonAttackResistant } from "../effects/nonAttackResistance";
import { capUseSteps } from "../events/gameEvents";
import { raiseDamagePacket, type DamageInstance, type DamagePacketEvent } from "../events/damageEvents";
import type { EventLoop } from "../events/loop";
import type { SelfCheckContext } from "../selfCheck";
import { resolveDamageCascade } from "../tables";
import { applyDamageScale, isPhysical, mitigate, routeFinalDamage } from "./damage";
import type { DamageReport } from "./types";

/** Named args for {@link applyDamagePacket}. */
export type ApplyDamagePacketArgs = {
  packet: DamagePacketEvent;
  /** The state the packet lands on: the resolution's state as the steps before it left it. */
  state: EncounterState;
  /** Draws the cascades' table rolls. */
  dice: DiceSource;
  /** Exposed, Shredded, Resistance and Immunity are folded from it. Omitted → `SEED_OVERLAY`. */
  overlay?: LoadedOverlay;
  /** Threaded to the cascades' System Trauma pick. */
  choices?: RecordedAnswers;
  interactive?: boolean;
  /** Threaded to the structure and overheating tables' self-checks. */
  selfCheck?: SelfCheckContext;
  /** The witnesses' provenance, when the site names one (GM damage's `effect` source). */
  witness?: Pick<DamageStep, "source">;
  /** The intent's loop, when the site holds one: the spend of a capped grant the packet used is raised into it. */
  loop?: EventLoop;
};

/** One instance as it landed. */
export type PacketInstance = {
  /** The `damage` witness: the instance's dice, mitigation and final amount, flagged `immune` when Immunity cancelled it. */
  witness: DamageStep;
  /** Overshield, then the HP or heat cascade, then the burn mark. Empty when Immune. */
  landing: ResolvedStep[];
};

export type DamagePacketResult = {
  instances: PacketInstance[];
  /** Each instance's witness, then its landing, in instance order; then the spend of any capped Resistance or Immunity the packet used. */
  steps: ResolvedStep[];
  warnings: string[];
  /** The packet as an `on:"damage"` fire reads it: its target, the damage dealt before Overshield (0 when Immune), and its first type. */
  report: DamageReport;
  /** The damage cascade destroyed the target. */
  destroyed: boolean;
};

/** Run `packet` through the damage pipeline over `state`. Pure: no event, no loop. */
export function applyDamagePacket(args: ApplyDamagePacketArgs): DamagePacketResult {
  const { packet, state, dice, overlay, choices, interactive, selfCheck } = args;
  const { source, target: targetId, fromAttack, ap, irreducible } = packet;
  const entry = state.combatants[targetId];
  if (entry === undefined) throw new Error(`applyDamagePacket: no combatant ${targetId}`);
  if (packet.substituteStructure) {
    const cascade = resolveDamageCascade({ target: entry, amount: 0, dice, ...(choices !== undefined ? { choices } : {}), ...(interactive !== undefined ? { interactive } : {}), turnOrdinal: state.activation.turnOrdinal, ...(selfCheck !== undefined ? { selfCheck } : {}), substituteStructurePoint: true });
    const dealt = cascade.steps.some((s) => s.op === "structure_delta" && s.target === targetId);
    return { instances: [], steps: cascade.steps, warnings: cascade.warnings, report: { id: targetId, final: dealt ? 1 : 0, dtype: "kinetic" }, destroyed: cascade.destroyed };
  }
  const exposed = nonAttackExposed({ state, targetId, attackerId: source, overlay });
  let armorLeft = ap || irreducible ? 0 : nonAttackArmor({ state, targetId, attackerId: source, overlay, armor: entry.base.armor });
  let reduceLeft = irreducible ? 0 : Math.max(0, packet.reduce ?? 0);
  const instances: PacketInstance[] = [];
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  let dealt = 0;
  let destroyed = false;
  const caps = new Map<string, { carrier: string; key: string }>();
  for (const inst of packet.instances) {
    const { dtype } = inst;
    // Heat is not damage, so damage Immunity never cancels it (the heat track has its own rules).
    const immune = !irreducible && dtype !== "heat" && nonAttackImmune({ state, targetId, dtype, overlay });
    const resistant = !irreducible && inst.resisted !== true && nonAttackResistant({ state, targetId, attackerId: source, fromAttack, dtype, overlay });
    const landed = landDamageInstance({
      state,
      // Each instance lands on what the earlier ones left.
      target: steps.length > 0 ? applyResolved(state, steps).combatants[targetId]! : entry,
      instance: { ...inst, amount: exposed && isPhysical(dtype) ? inst.amount * 2 : inst.amount },
      ap,
      armor: armorLeft,
      resistant,
      reduce: reduceLeft,
      immune,
      ...(args.witness !== undefined ? { witness: args.witness } : {}),
      route: { dice, ...(choices !== undefined ? { choices } : {}), ...(interactive !== undefined ? { interactive } : {}), ...(selfCheck !== undefined ? { selfCheck } : {}), from: source ?? "gm" },
    });
    armorLeft -= landed.armorUsed;
    reduceLeft -= landed.reduced;
    instances.push({ witness: landed.witness, landing: landed.landing });
    steps.push(landed.witness, ...landed.landing);
    warnings.push(...(immune ? [nonAttackImmuneWarning(entry.name, dtype)] : landed.warnings));
    if (!immune) dealt += landed.witness.final;
    destroyed ||= landed.destroyed;
    // A capped grant is spent by the packet that applies it, once however many instances it touched (R7).
    for (const cap of nonAttackGrantCaps({ state, targetId, attackerId: source, fromAttack, dtype, overlay, resisted: landed.witness.resisted, immune })) caps.set(`${cap.carrier}:${cap.key}`, cap);
  }
  steps.push(...capUseSteps([...caps.values()], args.loop));
  return { instances, steps, warnings, report: { id: targetId, final: dealt, dtype: packet.instances[0]?.dtype ?? "kinetic" }, destroyed };
}

/** Named args for {@link landDamageInstance}: one instance and the mitigation facts the caller settled for it. */
export type LandDamageInstanceArgs = {
  /** The state the rules read: its turn ordinal stamps the cascades. */
  state: EncounterState;
  /** Who takes it, as the steps before it left them: its Overshield is read off it. */
  target: Combatant;
  /** The instance, Exposed already applied to `amount`. */
  instance: DamageInstance;
  /** Recorded on the witness. Armor is already off `armor` when set. */
  ap: boolean;
  /** The Armor still available against this instance (Shredded and AP already applied). */
  armor: number;
  resistant: boolean;
  /** Other deductions still available (Reduce), taken after Resistance. */
  reduce: number;
  immune: boolean;
  /**
   * An attack's half damage (`RequestAttack.damageScale`): attacker-side, so it comes off first,
   * before Armor (Core p.67 step 1, `event-engine-damage` R4). The witness's `preMitigation` keeps
   * the unhalved amount.
   */
  scale?: RequestAttack["damageScale"];
  /** The witness's provenance and Reliable lift, when it has them. */
  witness?: Pick<DamageStep, "reliable" | "source">;
  /** The cascades' dice, answers and self-checks, and who dealt the damage (`routeFinalDamage`). */
  route: { dice: DiceSource; choices?: RecordedAnswers; interactive?: boolean; selfCheck?: SelfCheckContext; from: string; mark?: boolean };
};

/** One instance mitigated and landed: its witness, its landing (empty when Immune), what it used, and its amount after Armor and Resistance (`mitigated`). */
export type LandedInstance = PacketInstance & { warnings: string[]; armorUsed: number; reduced: number; mitigated: number; destroyed: boolean };

/**
 * **One instance through D2's steps after Exposed**: the attack's half, Armor, Resistance, Reduce,
 * then Immunity, Overshield and HP or heat. The caller settles which facts apply (the packet
 * folds them off the target; the attack hands it its own fold and its window's outcome) and how
 * much Armor and Reduce are left. Its warnings are the landing's; an Immune instance's warning
 * is the caller's to word.
 */
export function landDamageInstance(args: LandDamageInstanceArgs): LandedInstance {
  const { state, target, instance: inst, immune, route } = args;
  const mitigated = mitigateDamageInstance(args);
  if (immune) return { ...mitigated, landing: [], warnings: [], destroyed: false };
  const routed = routeFinalDamage({ target, dtype: inst.dtype, final: mitigated.witness.final, turnOrdinal: state.activation.turnOrdinal, ...route });
  return { ...mitigated, landing: routed.steps, warnings: routed.warnings, destroyed: routed.destroyed };
}

/**
 * {@link landDamageInstance}'s mitigation alone: the witness and what it used, landing nothing. For
 * a site whose damage lands inside another cascade (Monstrosity Dismemberment's 1d6, taken by the
 * structure table that rolled it).
 */
export function mitigateDamageInstance(args: Omit<LandDamageInstanceArgs, "state" | "route">): Pick<LandedInstance, "witness" | "armorUsed" | "reduced" | "mitigated"> {
  const { target, instance: inst, ap, armor, resistant, reduce, immune, scale } = args;
  const { dtype, amount } = inst;
  const scaled = applyDamageScale(amount, scale);
  const mit = mitigate({ preMitigation: scaled, dtype, ap: false, targetArmor: armor, resistant });
  const reduced = Math.min(Math.max(0, reduce), mit.final);
  const witness: DamageStep = {
    op: "damage",
    dtype,
    dice: inst.dice ?? [],
    ...(inst.sides !== undefined ? { sides: inst.sides } : {}),
    bonus: inst.bonus ?? amount - (inst.dice ?? []).reduce((a, b) => a + b, 0),
    preMitigation: amount,
    ...(args.witness?.reliable !== undefined ? { reliable: args.witness.reliable } : {}),
    ap,
    armor: mit.armor,
    resisted: mit.resisted,
    final: mit.final - reduced,
    crit: inst.crit ?? false,
    ...(immune ? { immune: true } : {}),
    target: target.id,
    ...(args.witness?.source !== undefined ? { source: args.witness.source } : {}),
  };
  return { witness, armorUsed: Math.min(mit.armor, scaled), reduced, mitigated: mit.final };
}

/**
 * Deal `packet`: raised into `loop` as a `damage_packet` event when the site holds the intent's
 * loop, landed directly when it doesn't. Either way the pipeline is {@link applyDamagePacket}.
 */
export function dealDamagePacket(args: ApplyDamagePacketArgs & { loop: EventLoop | undefined }): DamagePacketResult {
  const { loop, ...rest } = args;
  return raiseDamagePacket({ loop, packet: rest.packet, land: (packet) => applyDamagePacket({ ...rest, packet, ...(loop !== undefined ? { loop } : {}) }) });
}
